/**
 * Better Auth Client Utilities
 *
 * This module provides client-side authentication utilities for React.
 * It creates an auth client that communicates with the Better Auth server.
 *
 * Features:
 * - Sign in/out functions
 * - useSession hook for reactive session state
 * - Automatic session refresh
 *
 * @example
 * // In a React component
 * import { useSession, signIn, signOut } from '@pagelane/auth/client';
 *
 * function UserMenu() {
 *   const { data: session, isPending } = useSession();
 *   if (isPending) return <Spinner />;
 *   if (!session) return <Button onClick={() => signIn.email(...)}>Login</Button>;
 *   return <Button onClick={() => signOut()}>Logout</Button>;
 * }
 */
import { createAuthClient } from 'better-auth/react';

/**
 * Better Auth client instance for React
 *
 * Uses React-specific client that provides:
 * - useSession hook with reactive updates
 * - Optimized re-renders on session changes
 * - Automatic session persistence
 */
export const authClient = createAuthClient({
  // Base URL defaults to same origin in browser
  // Explicitly set for SSR or when auth is on different domain
  baseURL: process.env.NEXT_PUBLIC_APP_URL || undefined,
});

// Export commonly used utilities for convenience
export const { signIn, signOut, useSession } = authClient;

// Re-export the full client for advanced use cases
export { authClient as client };
